import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const ExpiredUsersDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Controlla se l'utente è superadmin
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'supersaiyan';

  // Controlla immediatamente quando il superadmin fa login
  useEffect(() => {
    if (!isSuperAdmin || !user) return;

    const checkExpiredUsers = async () => {
      try {
        logger.debug('🔍 [ExpiredUsersDialog] Controllo utenti scaduti...');
        const response = await api.get('/expired-users');
        logger.debug('📋 [ExpiredUsersDialog] Utenti scaduti trovati:', response.data?.length || 0);
        
        if (response.data && response.data.length > 0) {
          setExpiredUsers(response.data);
          setOpen(true);
          logger.debug('✅ [ExpiredUsersDialog] Dialog aperto con', response.data.length, 'utenti scaduti');
        } else {
          logger.debug('ℹ️ [ExpiredUsersDialog] Nessun utente scaduto trovato');
        }
      } catch (err) {
        logger.error('❌ [ExpiredUsersDialog] Errore nel controllo utenti scaduti:', err);
      }
    };

    // Controlla immediatamente quando l'utente superadmin viene caricato
    checkExpiredUsers();

    // Controlla anche ogni 24 ore come backup
    const interval = setInterval(checkExpiredUsers, 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isSuperAdmin, user?.id]); // Dipende da user.id per triggerare quando l'utente cambia

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    if (!window.confirm(`Sei sicuro di voler eliminare definitivamente l'account admin di ${selectedUser.name}?\n\nATTENZIONE: Questa azione eliminerà TUTTI gli utenti dell'azienda "${selectedUser.company?.name || selectedUser.Company?.name || 'N/A'}" e disabiliterà l'azienda.\n\nQuesta azione è irreversibile.`)) {
      handleMenuClose();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.delete(`/expired-users/${selectedUser.id}`);
      setSuccess(`Account di ${selectedUser.name} eliminato con successo`);
      setExpiredUsers(expiredUsers.filter(u => u.id !== selectedUser.id));
      if (expiredUsers.length === 1) {
        setOpen(false);
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nell\'eliminazione dell\'account');
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleSendEmail = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    try {
      await api.post(`/expired-users/${selectedUser.id}/send-email`);
      setSuccess(`Email inviata con successo a ${selectedUser.email}`);
      // Aggiorna lo stato dell'utente
      setExpiredUsers(expiredUsers.map(u => 
        u.id === selectedUser.id 
          ? { ...u, emailNotification: { sentAt: new Date() } }
          : u
      ));
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nell\'invio dell\'email');
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedUser) return;
    handleMenuClose();

    // Se l'utente ha già un numero di telefono, invia direttamente
    if (selectedUser.phoneNumber) {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post(`/expired-users/${selectedUser.id}/send-whatsapp`, {});
        
        // Apri WhatsApp
        window.open(response.data.whatsappUrl, '_blank');
        
        setSuccess(`Messaggio WhatsApp generato per ${selectedUser.name}`);
        
        // Aggiorna lo stato dell'utente
        setExpiredUsers(expiredUsers.map(u => 
          u.id === selectedUser.id 
            ? { ...u, whatsappNotification: { sentAt: new Date() } }
            : u
        ));
      } catch (err) {
        setError(err.response?.data?.msg || 'Errore nella generazione del messaggio WhatsApp');
      } finally {
        setLoading(false);
      }
    } else {
      // Se non ha numero, mostra il dialog per inserirlo
      setPhoneDialogOpen(true);
    }
  };

  const handleWhatsAppConfirm = async () => {
    if (!phoneNumber.trim()) {
      setError('Inserisci un numero di telefono valido');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/expired-users/${selectedUser.id}/send-whatsapp`, {
        phoneNumber
      });
      
      // Apri WhatsApp
      window.open(response.data.whatsappUrl, '_blank');
      
      setSuccess(`Messaggio WhatsApp generato per ${selectedUser.name}`);
      setPhoneDialogOpen(false);
      setPhoneNumber('');
      
      // Aggiorna lo stato dell'utente
      setExpiredUsers(expiredUsers.map(u => 
        u.id === selectedUser.id 
          ? { ...u, whatsappNotification: { sentAt: new Date() } }
          : u
      ));
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nella generazione del messaggio WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysExpired = (expirationDate) => {
    const now = new Date();
    const expired = new Date(expirationDate);
    const diffTime = now - expired;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!isSuperAdmin) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              ⚠️ Account Scaduti
            </Typography>
            <Chip 
              label={`${expiredUsers.length} utente/i`} 
              color="error" 
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            I seguenti utenti <strong>admin</strong> hanno account scaduti da più di 3 giorni. Seleziona un'azione per ciascun utente.
            <br />
            <strong>Nota:</strong> L'eliminazione di un admin eliminerà tutti gli utenti della sua azienda e disabiliterà l'azienda.
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}

          <List>
            {expiredUsers.map((expiredUser, index) => (
              <React.Fragment key={expiredUser.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {expiredUser.name}
                        </Typography>
                        <Chip 
                          label={`Scaduto da ${getDaysExpired(expiredUser.expirationDate)} giorni`}
                          color="error"
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" component="div">
                          Email: {expiredUser.email}
                        </Typography>
                        {expiredUser.phoneNumber && (
                          <Typography variant="body2" color="text.secondary" component="div">
                            Telefono: {expiredUser.phoneNumber}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" component="div">
                          Azienda: {expiredUser.Company?.name || expiredUser.company?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                          Scadenza: {formatDate(expiredUser.expirationDate)}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {expiredUser.emailNotification && (
                            <Chip 
                              label={`Email inviata il ${formatDate(expiredUser.emailNotification.sentAt)}`}
                              color="info"
                              size="small"
                            />
                          )}
                          {expiredUser.whatsappNotification && (
                            <Chip 
                              label={`WhatsApp inviato il ${formatDate(expiredUser.whatsappNotification.sentAt)}`}
                              color="info"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleMenuOpen(e, expiredUser)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < expiredUsers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleDelete}>
              <DeleteIcon sx={{ mr: 1 }} />
              Elimina Definitivamente
            </MenuItem>
            <MenuItem onClick={handleSendEmail} disabled={!!selectedUser?.emailNotification || loading}>
              <EmailIcon sx={{ mr: 1 }} />
              Invia Email
              {selectedUser?.emailNotification && ' (già inviata)'}
            </MenuItem>
            <MenuItem onClick={handleSendWhatsApp} disabled={!!selectedUser?.whatsappNotification || loading}>
              <WhatsAppIcon sx={{ mr: 1 }} />
              Invia WhatsApp
              {selectedUser?.whatsappNotification && ' (già inviato)'}
            </MenuItem>
          </Menu>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per numero WhatsApp */}
      <Dialog
        open={phoneDialogOpen}
        onClose={() => {
          setPhoneDialogOpen(false);
          setPhoneNumber('');
          setError(null);
        }}
      >
        <DialogTitle>Invia WhatsApp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Inserisci il numero di telefono di {selectedUser?.name} per inviare il messaggio WhatsApp.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            type="tel"
            label="Numero di Telefono"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Es: 3331234567 o +393331234567"
            margin="normal"
            helperText="Inserisci il numero con o senza prefisso internazionale"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPhoneDialogOpen(false);
            setPhoneNumber('');
            setError(null);
          }}>
            Annulla
          </Button>
          <Button 
            onClick={handleWhatsAppConfirm}
            variant="contained"
            disabled={loading || !phoneNumber.trim()}
          >
            {loading ? 'Invio...' : 'Invia WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpiredUsersDialog;

